<template>
    <q-expansion-item icon="architecture" label="Diseñador de Cámara" caption="Planta 2D y vista 3D"
        header-class="bg-grey-2 text-primary text-weight-medium" class="q-mb-md rounded-borders overflow-hidden">
        <q-card flat bordered>
            <q-card-section>
                <!-- Toolbar -->
                <div class="row q-gutter-md q-mb-md items-center">
                    <div class="col-auto" style="min-width: 140px;">
                        <GltnInputNumber v-model="camaraAltura" label="Alto (m)" />
                    </div>
                    <div class="col-auto q-pt-sm">
                        <span class="text-caption text-grey-7">
                            <q-icon name="grid_on" size="13px" class="q-mr-xs" />
                            1 cuadro = 1 m &nbsp;·&nbsp;
                            {{ vertices.length }} vértice{{ vertices.length !== 1 ? 's' : '' }}
                            <span v-if="isClosed" class="text-positive q-ml-xs">
                                <q-icon name="check_circle" size="13px" /> figura cerrada
                            </span>
                        </span>
                    </div>
                    <div class="col" />
                    <div class="col-auto row q-gutter-x-xs items-center">
                        <!-- Modo (solo cuando el perímetro está cerrado) -->
                        <template v-if="isClosed">
                            <q-btn :color="mode === 'perimetro' ? 'blue-6' : 'grey-7'" dense flat size="sm"
                                icon="crop_free" @click="mode = 'perimetro'">
                                <q-tooltip>Editar perímetro</q-tooltip>
                            </q-btn>
                            <q-separator vertical inset class="q-mx-xs" />
                        </template>
                        <q-btn :flat="!snapToGrid" :unelevated="snapToGrid" dense size="sm" icon="grid_4x4"
                            :color="snapToGrid ? 'primary' : 'grey-7'" @click="snapToGrid = !snapToGrid">
                            <q-tooltip>{{ snapToGrid ? 'Snap al grid: ON' : 'Snap al grid: OFF' }}</q-tooltip>
                        </q-btn>
                        <q-separator vertical inset class="q-mx-xs" />
                        <q-btn flat dense size="sm" icon="undo" color="grey-5"
                            :disable="vertices.length === 0 && paredesInternas.length === 0" @click="undoLast">
                            <q-tooltip>Deshacer</q-tooltip>
                        </q-btn>
                        <q-btn flat dense size="sm" icon="delete" color="negative" @click="clearFigure">
                            <q-tooltip>Limpiar todo</q-tooltip>
                        </q-btn>
                        <q-btn unelevated dense size="sm" icon="check_circle" color="positive"
                            :disable="vertices.length < 3 || isClosed" label="Cerrar figura" @click="closeFigure" />
                    </div>
                </div>

                <!-- Panels -->
                <div class="row q-col-gutter-md">
                    <!-- Columna izquierda: 2D + 3D -->
                    <div class="col-12 col-md-7 column q-gutter-y-md">
                    <!-- 2D Drawing -->
                    <div>
                        <div class="designer-section-label q-mb-xs">
                            <template v-if="!isClosed">
                                <q-icon name="draw" size="13px" class="q-mr-xs" />
                                Planta 2D — click para agregar vértices · click en
                                <q-icon name="circle" size="10px" color="positive" /> o doble-click para cerrar
                            </template>
                            <template v-else-if="mode === 'interna'">
                                <q-icon name="linear_scale" size="12px" class="q-mr-xs text-amber-5" />
                                Pared interna — click para trazar puntos · doble-click o
                                <q-badge color="amber-8" label="Finalizar pared" /> para cerrar el segmento
                            </template>
                            <template v-else>
                                <q-icon name="drag_indicator" size="12px" class="q-mr-xs" />
                                Arrastrá los vértices para editar el perímetro
                            </template>
                        </div>
                        <!-- Controles de zoom -->
                        <div class="row items-center q-gutter-x-xs q-mb-xs">
                            <q-btn flat round dense icon="zoom_out" size="xs" color="grey-7" :disable="zoom <= ZOOM_MIN"
                                @click="zoomOut">
                                <q-tooltip>Alejar</q-tooltip>
                            </q-btn>
                            <q-btn flat dense size="xs" color="primary" class="q-px-xs"
                                style="min-width:44px;font-size:11px;" @click="zoomReset">
                                {{ Math.round(zoom * 100) }}%
                                <q-tooltip>Restaurar zoom</q-tooltip>
                            </q-btn>
                            <q-btn flat round dense icon="zoom_in" size="xs" color="grey-7" :disable="zoom >= ZOOM_MAX"
                                @click="zoomIn">
                                <q-tooltip>Acercar</q-tooltip>
                            </q-btn>
                            <span class="text-caption text-grey-6 q-ml-xs">scroll para zoom</span>
                        </div>
                        <div style="position: relative;">
                            <canvas ref="canvas2d" :width="canvasSize" :height="canvasSize"
                                :style="{ cursor: canvasCursor, borderRadius: '6px', display: 'block', width: canvasSize + 'px', height: canvasSize + 'px', aspectRatio: '1 / 1' }"
                                @click.stop="onCanvasClick" @mousedown.stop="onCanvasMouseDown"
                                @mouseup.stop="onCanvasMouseUp" @mousemove="onCanvasMouseMove"
                                @mouseleave="onCanvasMouseLeave" @dblclick.stop.prevent="onCanvasDblClick"
                                @wheel.prevent="e => e.deltaY < 0 ? zoomIn() : zoomOut()" />

                            <!-- Barra flotante de herramientas (visible solo cuando la figura está cerrada) -->
                            <Transition name="tool-bar">
                                <div v-if="isClosed" class="tools-floating-bar">
                                    <!-- Grupo: Paredes internas -->
                                    <div class="tools-group">
                                        <span class="tools-group-label">Paredes</span>
                                        <q-btn :unelevated="mode === 'interna'" :flat="mode !== 'interna'" dense
                                            size="sm" icon="linear_scale"
                                            :color="mode === 'interna' ? 'amber-6' : 'grey-5'"
                                            @click="mode = mode === 'interna' ? 'perimetro' : 'interna'">
                                            <q-tooltip>Agregar pared interna</q-tooltip>
                                        </q-btn>
                                        <q-btn v-if="mode === 'interna' && internalWallInProgress.length >= 2"
                                            unelevated dense size="sm" icon="done" color="amber-8" label="Finalizar"
                                            @click="finishInternalWall">
                                            <q-tooltip>Finalizar pared interna</q-tooltip>
                                        </q-btn>
                                    </div>

                                    <div class="tools-separator" />

                                    <!-- Grupo: Puertas -->
                                    <div class="tools-group">
                                        <span class="tools-group-label">Puertas</span>
                                        <q-btn :unelevated="mode === 'puerta'" :flat="mode !== 'puerta'" dense size="sm"
                                            icon="door_front" :color="mode === 'puerta' ? 'teal-5' : 'grey-5'"
                                            @click="mode = mode === 'puerta' ? 'perimetro' : 'puerta'">
                                            <q-tooltip>Agregar puerta</q-tooltip>
                                        </q-btn>

                                        <!-- Dirección de apertura (visible al activar modo puerta) -->
                                        <template v-if="mode === 'puerta'">
                                            <q-separator vertical dark class="q-mx-xs" />
                                            <div class="row items-center q-gutter-x-xs no-wrap">
                                                <span class="tools-group-label">Apertura</span>
                                                <q-btn v-for="apt in [
                                                    { val: 'in-left', icon: 'arrow_back', label: 'Interior izq.' },
                                                    { val: 'in-right', icon: 'arrow_forward', label: 'Interior der.' },
                                                    { val: 'out-left', icon: 'arrow_back', label: 'Exterior izq.' },
                                                    { val: 'out-right', icon: 'arrow_forward', label: 'Exterior der.' },
                                                ]" :key="apt.val" :unelevated="aperturaPuerta === apt.val"
                                                    :flat="aperturaPuerta !== apt.val" dense size="xs"
                                                    :icon="apt.val.startsWith('out') ? 'arrow_outward' : 'subdirectory_arrow_right'"
                                                    :color="aperturaPuerta === apt.val ? 'teal-5' : 'grey-5'"
                                                    :label="apt.val === 'in-left' ? 'Int↙' : apt.val === 'in-right' ? 'Int↘' : apt.val === 'out-left' ? 'Ext↙' : 'Ext↘'"
                                                    @click="aperturaPuerta = apt.val">
                                                    <q-tooltip>{{ apt.label }}</q-tooltip>
                                                </q-btn>
                                                <!-- <q-separator vertical dark class="q-mx-xs" />
                                                <span class="tools-group-label">Ancho</span>
                                                <input v-model.number="nuevaAnchoPuerta" type="number" min="0.3" max="5"
                                                    step="0.1" class="wall-len-input wall-len-input--dark"
                                                    style="width:46px" />
                                                <span class="tools-group-label">m</span> -->
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </Transition>
                        </div><!-- end canvas wrapper -->
                    </div>

                    <!-- 3D Preview -->
                    <div>
                        <div class="designer-section-label q-mb-xs">
                            <q-icon name="view_in_ar" size="13px" class="q-mr-xs" />
                            Vista 3D — arrastrará para rotar · scroll para zoom
                        </div>
                        <div style="height: 420px; border-radius: 6px; overflow: hidden;">
                            <TresCanvas clear-color="#1a1a2e">
                                <TresPerspectiveCamera :position="cameraPosition" :fov="50" :look-at="[0, 0, 0]" />
                                <CamaraOrbitControls />
                                <TresAmbientLight :intensity="0.8" />
                                <TresDirectionalLight :position="[8, 10, 8]" :intensity="1.2" />
                                <TresDirectionalLight :position="[-5, 5, -5]" :intensity="0.4" />
                                <primitive :object="gridHelper3d" />
                                <primitive v-if="extrudedMesh" :object="extrudedMesh" />
                                <primitive v-if="edgesMesh" :object="edgesMesh" />
                                <primitive v-if="internalWallsGroup" :object="internalWallsGroup" />
                                <primitive v-if="doorsGroup3d" :object="doorsGroup3d" />
                            </TresCanvas>
                        </div>
                    </div>
                    </div><!-- end columna izquierda -->

                    <!-- Columna derecha: Listado de paneles -->
                    <div class="col-12 col-md-5">
                        <div class="designer-section-label q-mb-xs">
                            <q-icon name="straighten" size="13px" class="q-mr-xs" />
                            Paredes
                            <span v-if="paredes.length" class="q-ml-xs">({{ paredes.length }})</span>
                        </div>

                        <div v-if="paredes.length === 0" class="text-caption text-grey-7 text-center q-mt-lg"
                            style="opacity: 0.5;">
                            <q-icon name="edit" size="20px" class="q-mb-xs" /><br>
                            Dibujá la planta para ver las paredes
                        </div>

                        <q-list v-else dense separator class="rounded-borders"
                            style="max-height: 950px; overflow-y: auto;">
                            <q-item v-for="pared in paredes" :key="pared.index" class="q-py-xs">
                                <q-item-section avatar>
                                    <q-avatar size="24px" :color="pared.isCierre ? 'orange-9' : 'blue-9'"
                                        text-color="white" font-size="10px">
                                        {{ pared.index + 1 }}
                                    </q-avatar>
                                </q-item-section>
                                <q-item-section>
                                    <q-item-label class="text-caption text-grey-9">
                                        {{ pared.isCierre ? 'Cierre' : `Pared ${pared.index + 1}` }}
                                    </q-item-label>
                                    <q-item-label caption class="text-grey-8 row items-center no-wrap q-gutter-x-xs">
                                        <q-icon name="straighten" size="11px" />
                                        <input type="number" :value="pared.longitud.toFixed(2)" min="0.01" step="0.01"
                                            class="wall-len-input"
                                            @change="e => setWallLength(pared.index, Number(e.target.value))"
                                            @keyup.enter="e => e.target.blur()" />
                                        <span>m</span>
                                    </q-item-label>
                                    <!-- <q-item-label caption class="text-grey-7">
                                        <q-icon name="crop_square" size="11px" class="q-mr-xs" />
                                        {{ pared.superficie.toFixed(2) }} m²
                                    </q-item-label>
                                    <q-item-label v-if="panelesParaLongitud(pared.longitud) !== null" caption
                                        class="text-blue-8">
                                        <q-icon name="view_column" size="11px" class="q-mr-xs" />
                                        {{ panelesParaLongitud(pared.longitud) }} panel{{
                                            panelesParaLongitud(pared.longitud) !== 1 ?
                                        'es' : '' }}
                                        <span class="text-grey-6">({{ panelAncho }}m c/u)</span>
                                        <span v-if="sobrante(pared.longitud) > 0.001" class="text-orange-7 q-ml-xs">
                                            · sobrante: {{ sobrante(pared.longitud).toFixed(2) }}m
                                        </span>
                                        <span v-else class="text-positive q-ml-xs">· ajuste exacto</span>
                                    </q-item-label> -->
                                </q-item-section>
                            </q-item>
                            <!-- Paredes internas -->
                            <template v-if="paredesInternasConMedidas.length > 0">
                                <q-item class="q-py-xs bg-amber-10">
                                    <q-item-section>
                                        <q-item-label class="text-caption text-amber-3 text-weight-bold">
                                            <q-icon name="linear_scale" size="12px" class="q-mr-xs" />
                                            Paredes internas ({{ paredesInternasConMedidas.length }})
                                        </q-item-label>
                                    </q-item-section>
                                </q-item>
                                <q-item v-for="pi in paredesInternasConMedidas" :key="`pi-${pi.id}`" class="q-py-xs">
                                    <q-item-section avatar>
                                        <q-avatar size="24px" color="amber-9" text-color="white" font-size="10px">
                                            {{ pi.order }}
                                        </q-avatar>
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label class="text-caption text-orange-9">
                                            Pared int. {{ pi.order }}
                                        </q-item-label>
                                        <!-- <q-item-label caption
                                            class="text-grey-8 row items-center no-wrap q-gutter-x-xs">
                                            <q-icon name="straighten" size="11px" />
                                            <input type="number" :value="pi.longitud.toFixed(2)" min="0.01" step="0.01"
                                                class="wall-len-input wall-len-input--amber"
                                                @change="e => setInternalWallLength(pi.id, Number(e.target.value))"
                                                @keyup.enter="e => e.target.blur()" />
                                            <span>m · {{ pi.superficie.toFixed(2) }} m²</span>
                                        </q-item-label>
                                        <q-item-label v-if="panelesParaLongitud(pi.longitud) !== null" caption
                                            class="text-blue-8">
                                            <q-icon name="view_column" size="11px" class="q-mr-xs" />
                                            {{ panelesParaLongitud(pi.longitud) }} panel{{
                                            panelesParaLongitud(pi.longitud) !== 1 ? 'es' : '' }}
                                            <span v-if="sobrante(pi.longitud) > 0.001" class="text-orange-7 q-ml-xs">
                                                · sobrante: {{ sobrante(pi.longitud).toFixed(2) }}m
                                            </span>
                                            <span v-else class="text-positive q-ml-xs">· ajuste exacto</span>
                                        </q-item-label> -->
                                    </q-item-section>
                                    <q-item-section side>
                                        <q-btn flat dense round size="xs" icon="delete" color="negative"
                                            @click="removeInternalWall(pi.id)" />
                                    </q-item-section>
                                </q-item>
                            </template>
                            <!-- Puertas -->
                            <template v-if="puertas.length > 0">
                                <q-item class="q-py-xs bg-teal-10">
                                    <q-item-section>
                                        <q-item-label class="text-caption text-teal-3 text-weight-bold">
                                            <q-icon name="door_front" size="12px" class="q-mr-xs" />
                                            Puertas ({{ puertas.length }})
                                        </q-item-label>
                                    </q-item-section>
                                </q-item>
                                <q-item v-for="puerta in puertas" :key="`puerta-${puerta.id}`" class="q-py-xs">
                                    <q-item-section avatar>
                                        <q-avatar size="24px" color="teal-7" text-color="white" font-size="10px">
                                            {{ puertas.indexOf(puerta) + 1 }}
                                        </q-avatar>
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label class="text-caption text-teal-9">
                                            Puerta {{ puertas.indexOf(puerta) + 1 }}
                                            <q-badge outline color="teal-7" class="q-ml-xs"
                                                :label="aperturaText(puerta.apertura)" />
                                        </q-item-label>
                                        <!-- Selector de apertura por puerta -->
                                        <q-item-label caption class="row q-gutter-x-xs q-mt-xs no-wrap">
                                            <q-btn v-for="apt in [
                                                { val: 'in-left', label: 'Int↙' },
                                                { val: 'in-right', label: 'Int↘' },
                                                { val: 'out-left', label: 'Ext↙' },
                                                { val: 'out-right', label: 'Ext↘' },
                                            ]" :key="apt.val" :unelevated="puerta.apertura === apt.val"
                                                :flat="puerta.apertura !== apt.val" dense size="xs"
                                                :color="puerta.apertura === apt.val ? 'teal-6' : 'grey-5'"
                                                :label="apt.label" @click="setPuertaApertura(puerta.id, apt.val)" />
                                        </q-item-label>
                                        <div class="q-mt-xs">
                                            <GltnSelectPuerta
                                                dense
                                                :modelValue="puerta.producto"
                                                label="Producto puerta"
                                                @update:modelValue="setPuertaProducto(puerta.id, $event)"
                                            />
                                        </div>
                                    </q-item-section>
                                    <q-item-section side>
                                        <q-btn flat dense round size="xs" icon="delete" color="negative"
                                            @click="removePuerta(puerta.id)" />
                                    </q-item-section>
                                </q-item>
                            </template>
                            <!-- Totales perímetro -->
                            <q-separator />
                            <!-- <q-item class="q-py-sm designer-totals-row">
                                <q-item-section>
                                    <q-item-label class="text-caption text-blue-2 text-weight-bold q-mb-xs">
                                        Perímetro total
                                    </q-item-label>
                                    <q-item-label class="text-white text-weight-bold">
                                        {{ perimetroTotal.toFixed(2) }} m
                                    </q-item-label>
                                </q-item-section>
                                <q-item-section>
                                    <q-item-label class="text-caption text-blue-2 text-weight-bold q-mb-xs">
                                        Sup. total paneles pared
                                    </q-item-label>
                                    <q-item-label class="text-white text-weight-bold">
                                        {{ superficieTotal.toFixed(2) }} m²
                                    </q-item-label>
                                </q-item-section>
                                <q-item-section v-if="isClosed">
                                    <q-item-label class="text-caption text-blue-2 text-weight-bold q-mb-xs">
                                        Sup. cámara
                                    </q-item-label>
                                    <q-item-label class="text-white text-weight-bold">
                                        {{ superficieCamara.toFixed(2) }} m²
                                    </q-item-label>
                                </q-item-section>
                                <q-item-section v-if="totalPanelesCount !== null">
                                    <q-item-label class="text-caption text-blue-2 text-weight-bold q-mb-xs">
                                        Total paneles
                                    </q-item-label>
                                    <q-item-label class="text-white text-weight-bold">
                                        {{ totalPanelesCount }}
                                    </q-item-label>
                                </q-item-section>
                            </q-item> -->
                            <!-- Esquinas -->
                            <q-item v-if="isClosed && esquinasInfo.total > 0" class="q-py-sm designer-corners-row">
                                <q-item-section>
                                    <q-item-label class="text-caption text-weight-bold q-mb-xs designer-corners-label">
                                        <q-icon name="crop_square" size="11px" class="q-mr-xs" />
                                        Esquinas
                                    </q-item-label>
                                    <q-item-label class="text-white text-weight-bold">
                                        {{ esquinasInfo.total }}
                                    </q-item-label>
                                </q-item-section>
                                <q-item-section>
                                    <q-item-label class="text-caption text-weight-bold q-mb-xs designer-corners-label">
                                        <q-icon name="subdirectory_arrow_right" size="11px" class="q-mr-xs" />
                                        Internas
                                        <q-tooltip>Cóncavas (ángulo entrante)</q-tooltip>
                                    </q-item-label>
                                    <q-badge color="indigo-5" class="text-weight-bold q-pa-xs">
                                        {{ esquinasInfo.internas }}
                                    </q-badge>
                                </q-item-section>
                                <q-item-section>
                                    <q-item-label class="text-caption text-weight-bold q-mb-xs designer-corners-label">
                                        <q-icon name="subdirectory_arrow_left" size="11px" class="q-mr-xs" />
                                        Externas
                                        <q-tooltip>Convexas (ángulo saliente)</q-tooltip>
                                    </q-item-label>
                                    <q-badge color="orange-7" class="text-weight-bold q-pa-xs">
                                        {{ esquinasInfo.externas }}
                                    </q-badge>
                                </q-item-section>
                            </q-item>

                        </q-list>
                    </div><!-- end columna derecha -->
                </div>
            </q-card-section>
        </q-card>
    </q-expansion-item>
</template>

<script setup>
import {
    ref, computed, shallowRef, watch, watchEffect,
    onMounted, onUnmounted, nextTick, defineComponent,
} from 'vue';
import { TresCanvas, useTres, useLoop } from '@tresjs/core';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import GltnInputNumber from '@/components/form/GltnInputNumber.vue';
import numberFormat from '@/helpers/numberFormat.js';
import GltnSelectPuerta from '@/components/form/GltnSelectPuerta.vue';


const props = defineProps({
    panelData: {
        type: Object,
        required: true,
    },
    design: {
        type: Object,
        default: null,
    },
});
const panelAncho = computed(() => numberFormat(parseFloat(props.panelData.ancho), true));
const emit = defineEmits(['update:design']);

// ─── Estado reactivo ──────────────────────────────────────────────────────────
const camaraAltura = ref(3);
const vertices = ref([]);
const mousePos = ref({ x: -1, y: -1 });
const isClosed = ref(false);
const mode = ref('perimetro'); // 'perimetro' | 'interna' | 'puerta'
const paredesInternas = ref([]); // [{ id, points: [{x,y}] }]
const internalWallInProgress = ref([]);
let nextInternalId = 1;

// ─── Estado puertas ───────────────────────────────────────────────────────────
const puertas = ref([]); // [{ id, wallIndex, t, ancho, apertura }]
const aperturaPuerta = ref('in-left'); // 'in-left'|'in-right'|'out-left'|'out-right'
const nuevaAnchoPuerta = ref(0.9);
let nextPuertaId = 1;

// ─── Drag state ───────────────────────────────────────────────────────────────
const dragTarget = ref(null); // null | { type:'perimeter', idx } | { type:'internal', wallIdx, ptIdx } | { type:'inprogress', ptIdx }
const dragMoved = ref(false);
const hoveredTarget = ref(null);

const snapToGrid = ref(false);
const zoom = ref(1);
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;

function zoomIn() { zoom.value = Math.min(ZOOM_MAX, Math.round((zoom.value + ZOOM_STEP) * 100) / 100); }
function zoomOut() { zoom.value = Math.max(ZOOM_MIN, Math.round((zoom.value - ZOOM_STEP) * 100) / 100); }
function zoomReset() { zoom.value = 1; }

const canvasSize = 500;
const gridStep = 50; // 50 px = 1 metro
const DRAG_HIT_RADIUS = 10;

function snapPos(pos) {
    if (!snapToGrid.value) return pos;

    return {
        x: Math.round(pos.x / gridStep) * gridStep,
        y: Math.round(pos.y / gridStep) * gridStep,
    };
}

const canvasCursor = computed(() => {
    if (dragTarget.value) return 'grabbing';
    if (hoveredTarget.value) return 'grab';

    if ((isClosed.value && mode.value === 'interna') || (isClosed.value && mode.value === 'puerta') || !isClosed.value) return 'crosshair';

    return 'default';
});

function findAnyVertexNear(pos) {
    const pIdx = vertices.value.findIndex(
        v => Math.hypot(v.x - pos.x, v.y - pos.y) < DRAG_HIT_RADIUS,
    );
    if (pIdx >= 0) return { type: 'perimeter', idx: pIdx };

    for (let wi = 0; wi < paredesInternas.value.length; wi++) {
        const ptIdx = paredesInternas.value[wi].points.findIndex(
            p => Math.hypot(p.x - pos.x, p.y - pos.y) < DRAG_HIT_RADIUS,
        );
        if (ptIdx >= 0) return { type: 'internal', wallIdx: wi, ptIdx };
    }

    const ipIdx = internalWallInProgress.value.findIndex(
        p => Math.hypot(p.x - pos.x, p.y - pos.y) < DRAG_HIT_RADIUS,
    );
    if (ipIdx >= 0) return { type: 'inprogress', ptIdx: ipIdx };

    const doorHandle = findDoorHandleNear(pos);
    if (doorHandle) return doorHandle;

    return null;
}

function findVertexNear(pos) {
    return vertices.value.findIndex(
        v => Math.hypot(v.x - pos.x, v.y - pos.y) < DRAG_HIT_RADIUS,
    );
}

// ─── Helpers geométricos para pared / puertas ─────────────────────────────────
function getWallSegments() {
    const verts = vertices.value;
    if (!isClosed.value || verts.length < 2) return [];

    return verts.map((a, i) => ({
        a,
        b: verts[(i + 1) % verts.length],
        wallIndex: i,
    }));
}

/**
 * Proyecta `pos` sobre todos los segmentos del perímetro.
 * @returns {{ wallIndex, t, sx, sy } | null}
 */
function snapToPared(pos, tolerance = 30) {
    const segs = getWallSegments();
    let best = null;
    let bestDist = tolerance;

    for (const { a, b, wallIndex } of segs) {
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const lenSq = dx * dx + dy * dy;
        if (lenSq < 0.001) continue;

        const t = Math.max(0, Math.min(1, ((pos.x - a.x) * dx + (pos.y - a.y) * dy) / lenSq));
        const sx = a.x + t * dx;
        const sy = a.y + t * dy;
        const dist = Math.hypot(pos.x - sx, pos.y - sy);

        if (dist < bestDist) {
            bestDist = dist;
            best = { wallIndex, t, sx, sy };
        }
    }

    return best;
}

/**
 * Calcula la geometría 2D de una puerta sobre el canvas.
 * @returns {{ p1, p2, center, normal, hingeLeft, hingeRight }}
 */
function puertaGeom(wallIndex, t, anchoPuerta) {
    const segs = getWallSegments();
    if (wallIndex >= segs.length) return null;

    const { a, b } = segs[wallIndex];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const segLen = Math.hypot(dx, dy);
    if (segLen < 0.001) return null;

    const ux = dx / segLen;
    const uy = dy / segLen;
    // Normal exterior (apunta a la derecha del segmento, sentido del recorrido del perímetro)
    const nx = -uy;
    const ny = ux;

    const anchoPx = anchoPuerta * gridStep;
    const halfAncho = anchoPx / 2;

    const cx = a.x + t * dx;
    const cy = a.y + t * dy;

    // Clamping: no salirse del segmento
    const tClamped = Math.max(halfAncho / segLen, Math.min(1 - halfAncho / segLen, t));
    const cxC = a.x + tClamped * dx;
    const cyC = a.y + tClamped * dy;

    return {
        p1: { x: cxC - ux * halfAncho, y: cyC - uy * halfAncho },
        p2: { x: cxC + ux * halfAncho, y: cyC + uy * halfAncho },
        center: { x: cxC, y: cyC },
        normal: { x: nx, y: ny },
        ux,
        uy,
        anchoPx,
        tClamped,
    };
}

function findDoorHandleNear(pos) {
    for (const puerta of puertas.value) {
        const geom = puertaGeom(puerta.wallIndex, puerta.t, puerta.ancho);
        if (!geom) continue;
        if (Math.hypot(pos.x - geom.center.x, pos.y - geom.center.y) < DRAG_HIT_RADIUS + 4) {
            return { type: 'door', doorId: puerta.id };
        }
    }

    return null;
}

// ─── Canvas 2D ────────────────────────────────────────────────────────────────
const canvas2d = ref(null);

function redraw() {
    const canvas = canvas2d.value;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const { width: w, height: h } = canvas;

    ctx.fillStyle = '#12122a';
    ctx.fillRect(0, 0, w, h);

    // Transformación de zoom centrada en el canvas
    ctx.save();
    ctx.translate(w / 2, h / 2);
    ctx.scale(zoom.value, zoom.value);
    ctx.translate(-w / 2, -h / 2);

    ctx.strokeStyle = 'rgba(66,165,245,0.10)';
    ctx.lineWidth = 0.5 / zoom.value;
    for (let x = 0; x <= w; x += gridStep) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
    }
    for (let y = 0; y <= h; y += gridStep) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
    }

    ctx.fillStyle = 'rgba(255,255,255,0.25)';
    ctx.font = `${10 / zoom.value}px Arial`;
    ctx.textBaseline = 'top';
    for (let i = 1; i * gridStep < w; i++) {
        ctx.fillText(`${i}m`, i * gridStep + 2, 2);
    }
    for (let i = 1; i * gridStep < h; i++) {
        ctx.fillText(`${i}m`, 2, i * gridStep + 2);
    }

    // Snap-to-grid: líneas guía + punto magnético
    if (snapToGrid.value && mousePos.value.x >= 0) {
        const sx = Math.round(mousePos.value.x / gridStep) * gridStep;
        const sy = Math.round(mousePos.value.y / gridStep) * gridStep;

        ctx.save();
        ctx.strokeStyle = 'rgba(76,175,80,0.55)';
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);

        ctx.beginPath();
        ctx.moveTo(sx, 0);
        ctx.lineTo(sx, h);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(0, sy);
        ctx.lineTo(w, sy);
        ctx.stroke();

        ctx.setLineDash([]);
        ctx.fillStyle = '#4CAF50';
        ctx.beginPath();
        ctx.arc(sx, sy, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    }

    const verts = vertices.value;

    if (isClosed.value && verts.length >= 3) {
        ctx.fillStyle = 'rgba(66,165,245,0.18)';
        ctx.beginPath();
        ctx.moveTo(verts[0].x, verts[0].y);
        for (let i = 1; i < verts.length; i++) {
            ctx.lineTo(verts[i].x, verts[i].y);
        }
        ctx.closePath();
        ctx.fill();
    }

    if (verts.length >= 2) {
        if (isClosed.value && puertas.value.length > 0) {
            // Dibujar perímetro con huecos de puertas
            const segs = getWallSegments();
            segs.forEach(({ a, b, wallIndex }) => {
                const doorOnWall = puertas.value
                    .map(p => ({ p, geom: puertaGeom(p.wallIndex, p.t, p.ancho) }))
                    .filter(({ p, geom }) => p.wallIndex === wallIndex && geom)
                    .sort((x, y) => x.geom.tClamped - y.geom.tClamped);

                if (doorOnWall.length === 0) {
                    ctx.strokeStyle = '#42A5F5';
                    ctx.lineWidth = 2;
                    ctx.beginPath();
                    ctx.moveTo(a.x, a.y);
                    ctx.lineTo(b.x, b.y);
                    ctx.stroke();
                    return;
                }

                const segLen = Math.hypot(b.x - a.x, b.y - a.y);
                const ux = (b.x - a.x) / segLen;
                const uy = (b.y - a.y) / segLen;

                ctx.strokeStyle = '#42A5F5';
                ctx.lineWidth = 2;

                let prevT = 0;
                doorOnWall.forEach(({ geom }) => {
                    const t1 = geom.tClamped - (geom.anchoPx / 2) / segLen;
                    const t2 = geom.tClamped + (geom.anchoPx / 2) / segLen;

                    // Segmento antes del hueco
                    if (t1 > prevT + 0.001) {
                        ctx.beginPath();
                        ctx.moveTo(a.x + prevT * (b.x - a.x), a.y + prevT * (b.y - a.y));
                        ctx.lineTo(a.x + t1 * (b.x - a.x), a.y + t1 * (b.y - a.y));
                        ctx.stroke();
                    }

                    prevT = t2;
                });

                // Último segmento después de la última puerta
                if (prevT < 0.999) {
                    ctx.beginPath();
                    ctx.moveTo(a.x + prevT * (b.x - a.x), a.y + prevT * (b.y - a.y));
                    ctx.lineTo(b.x, b.y);
                    ctx.stroke();
                }
            });
        } else {
            ctx.strokeStyle = '#42A5F5';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(verts[0].x, verts[0].y);
            for (let i = 1; i < verts.length; i++) {
                ctx.lineTo(verts[i].x, verts[i].y);
            }
            if (isClosed.value) {
                ctx.closePath();
            }
            ctx.stroke();
        }
    }

    if (!isClosed.value && verts.length >= 1 && mousePos.value.x >= 0) {
        ctx.strokeStyle = 'rgba(255,255,255,0.30)';
        ctx.lineWidth = 1;
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(verts[verts.length - 1].x, verts[verts.length - 1].y);
        ctx.lineTo(mousePos.value.x, mousePos.value.y);
        ctx.stroke();
        ctx.setLineDash([]);
    }

    // Labels de longitud al centro de cada segmento (perímetro)
    paredes.value.forEach((pared) => {
        const label = `${pared.longitud.toFixed(2)}m`;
        const name = pared.isCierre ? 'Cierre' : `Pared ${pared.index + 1}`;
        const px = pared.midX;
        const py = pared.midY;

        ctx.save();

        // Fondo compartido para las dos líneas
        ctx.font = 'bold 11px Arial';
        const tw = ctx.measureText(label).width;
        ctx.font = '9px Arial';
        const nw = ctx.measureText(name).width;
        const boxW = Math.max(tw, nw) + 8;
        const boxH = 28;

        ctx.fillStyle = 'rgba(10,20,50,0.80)';
        ctx.beginPath();
        ctx.roundRect(px - boxW / 2, py - boxH / 2, boxW, boxH, 4);
        ctx.fill();

        // Línea 1: medida
        ctx.font = 'bold 11px Arial';
        ctx.fillStyle = pared.isCierre ? '#FFA726' : '#FFFFFF';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, px, py - 6);

        // Línea 2: nombre de pared
        ctx.font = '9px Arial';
        ctx.fillStyle = pared.isCierre ? '#FFD54F' : 'rgba(180,210,255,0.85)';
        ctx.fillText(name, px, py + 7);

        ctx.restore();
    });

    // Paredes internas completadas
    paredesInternas.value.forEach((wall) => {
        if (wall.points.length < 2) return;

        ctx.strokeStyle = '#FFA726';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(wall.points[0].x, wall.points[0].y);
        for (let pi = 1; pi < wall.points.length; pi++) {
            ctx.lineTo(wall.points[pi].x, wall.points[pi].y);
        }
        ctx.stroke();

        for (let pi = 0; pi < wall.points.length - 1; pi++) {
            const a = wall.points[pi];
            const b = wall.points[pi + 1];
            const lon = Math.hypot((b.x - a.x) / gridStep, (b.y - a.y) / gridStep);
            const mx = (a.x + b.x) / 2;
            const my = (a.y + b.y) / 2;

            ctx.save();
            ctx.font = 'bold 11px Arial';
            const lbl = `${lon.toFixed(2)}m`;
            const tw2 = ctx.measureText(lbl).width;
            ctx.fillStyle = 'rgba(80,40,0,0.85)';
            ctx.beginPath();
            ctx.roundRect(mx - tw2 / 2 - 4, my - 9, tw2 + 8, 16, 4);
            ctx.fill();
            ctx.fillStyle = '#FFC107';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(lbl, mx, my);
            ctx.restore();
        }
    });

    // Pared interna en progreso
    if (mode.value === 'interna' && internalWallInProgress.value.length >= 1) {
        const iwp = internalWallInProgress.value;

        ctx.strokeStyle = '#FFA726';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(iwp[0].x, iwp[0].y);
        for (let pi = 1; pi < iwp.length; pi++) {
            ctx.lineTo(iwp[pi].x, iwp[pi].y);
        }
        ctx.stroke();

        if (mousePos.value.x >= 0) {
            ctx.strokeStyle = 'rgba(255,167,38,0.45)';
            ctx.lineWidth = 1;
            ctx.setLineDash([5, 5]);
            ctx.beginPath();
            ctx.moveTo(iwp[iwp.length - 1].x, iwp[iwp.length - 1].y);
            ctx.lineTo(mousePos.value.x, mousePos.value.y);
            ctx.stroke();
            ctx.setLineDash([]);
        }
    }

    function drawVertex(vx, vy, color, isActive) {
        if (isActive) {
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 1.5;
            ctx.setLineDash([3, 3]);
            ctx.beginPath();
            ctx.arc(vx, vy, 11, 0, Math.PI * 2);
            ctx.stroke();
            ctx.setLineDash([]);
        }

        ctx.fillStyle = isActive ? '#FFC107' : color;
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(vx, vy, isActive ? 7 : 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
    }

    verts.forEach((v, i) => {
        const isFirst = i === 0;

        if (!isClosed.value && verts.length >= 3 && isFirst) {
            ctx.strokeStyle = 'rgba(76,175,80,0.7)';
            ctx.lineWidth = 1;
            ctx.setLineDash([3, 3]);
            ctx.beginPath();
            ctx.arc(v.x, v.y, 14, 0, Math.PI * 2);
            ctx.stroke();
            ctx.setLineDash([]);
        }

        const isActive = (dragTarget.value?.type === 'perimeter' && dragTarget.value.idx === i)
            || (hoveredTarget.value?.type === 'perimeter' && hoveredTarget.value.idx === i);

        drawVertex(v.x, v.y, isFirst ? '#4CAF50' : '#42A5F5', isActive);

        ctx.fillStyle = 'rgba(255,255,255,0.55)';
        ctx.font = '10px monospace';
        ctx.textBaseline = 'bottom';
        ctx.textAlign = 'left';
        ctx.fillText(
            `(${(v.x / gridStep).toFixed(1)}, ${(v.y / gridStep).toFixed(1)})`,
            v.x + 7,
            v.y - 3,
        );
    });

    // Vértices de paredes internas completadas
    paredesInternas.value.forEach((wall, wi) => {
        wall.points.forEach((p, pi) => {
            const isActive = (dragTarget.value?.type === 'internal' && dragTarget.value.wallIdx === wi && dragTarget.value.ptIdx === pi)
                || (hoveredTarget.value?.type === 'internal' && hoveredTarget.value.wallIdx === wi && hoveredTarget.value.ptIdx === pi);

            drawVertex(p.x, p.y, '#FFA726', isActive);
        });
    });

    // Vértices de pared interna en progreso
    internalWallInProgress.value.forEach((p, pi) => {
        const isActive = (dragTarget.value?.type === 'inprogress' && dragTarget.value.ptIdx === pi)
            || (hoveredTarget.value?.type === 'inprogress' && hoveredTarget.value.ptIdx === pi);

        drawVertex(p.x, p.y, '#FFB74D', isActive);
    });

    // ─── Dibujar puertas ─────────────────────────────────────────────────────
    function drawDoor(geom, apertura, isPreview, isHovered) {
        if (!geom) return;

        const { p1, p2, center, normal, ux, uy, anchoPx } = geom;
        const alpha = isPreview ? 0.5 : 1;

        ctx.save();
        ctx.globalAlpha = alpha;

        // Hueco: líneas de jamb (pequeñas marcas perpendiculares)
        const jambLen = 5;
        ctx.strokeStyle = '#42A5F5';
        ctx.lineWidth = 2;
        // jamb en p1
        ctx.beginPath();
        ctx.moveTo(p1.x - normal.x * jambLen, p1.y - normal.y * jambLen);
        ctx.lineTo(p1.x + normal.x * jambLen, p1.y + normal.y * jambLen);
        ctx.stroke();
        // jamb en p2
        ctx.beginPath();
        ctx.moveTo(p2.x - normal.x * jambLen, p2.y - normal.y * jambLen);
        ctx.lineTo(p2.x + normal.x * jambLen, p2.y + normal.y * jambLen);
        ctx.stroke();

        // Batiente y arco de apertura
        // apertura: 'in-left' | 'in-right' | 'out-left' | 'out-right'
        const isIn = apertura.startsWith('in');
        const isLeft = apertura.endsWith('left');
        // gozne en p1 (left) o p2 (right)
        const hinge = isLeft ? p1 : p2;
        const freeEnd = isLeft ? p2 : p1;
        // dirección del batiente: interior o exterior
        const sign = isIn ? 1 : -1;
        const swingEndX = hinge.x + normal.x * sign * anchoPx;
        const swingEndY = hinge.y + normal.y * sign * anchoPx;

        // Línea del batiente
        ctx.strokeStyle = '#A5D6A7';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(hinge.x, hinge.y);
        ctx.lineTo(swingEndX, swingEndY);
        ctx.stroke();

        // Arco de apertura
        const angleHinge = Math.atan2(freeEnd.y - hinge.y, freeEnd.x - hinge.x);
        const angleSwing = Math.atan2(swingEndY - hinge.y, swingEndX - hinge.x);

        ctx.strokeStyle = 'rgba(165,214,167,0.6)';
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.arc(hinge.x, hinge.y, anchoPx, Math.min(angleHinge, angleSwing), Math.max(angleHinge, angleSwing));
        ctx.stroke();
        ctx.setLineDash([]);

        // Handle de arrastre
        const handleColor = isHovered ? '#FFC107' : '#E0F7FA';
        ctx.fillStyle = handleColor;
        ctx.strokeStyle = '#0097A7';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(center.x, center.y, 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // Icono de puerta (letra pequeña)
        ctx.fillStyle = '#00ACC1';
        ctx.font = 'bold 8px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('D', center.x, center.y);

        ctx.restore();
    }

    puertas.value.forEach((puerta) => {
        const geom = puertaGeom(puerta.wallIndex, puerta.t, puerta.ancho);
        const isHovered = hoveredTarget.value?.type === 'door' && hoveredTarget.value?.doorId === puerta.id;
        drawDoor(geom, puerta.apertura, false, isHovered);
    });

    // Preview de puerta si estamos en modo puerta
    if (mode.value === 'puerta' && mousePos.value.x >= 0 && !dragTarget.value) {
        const snap = snapToPared(mousePos.value);
        if (snap) {
            const geom = puertaGeom(snap.wallIndex, snap.t, nuevaAnchoPuerta.value);
            drawDoor(geom, aperturaPuerta.value, true, false);
        }
    }

    ctx.restore(); // fin zoom
}

watch([vertices, mousePos, isClosed, camaraAltura, mode, paredesInternas, internalWallInProgress, snapToGrid, zoom, puertas, aperturaPuerta, nuevaAnchoPuerta], () => nextTick(redraw), { deep: true });
onMounted(() => nextTick(redraw));

// ─── Paredes (segmentos entre vértices consecutivos) ─────────────────────────
const paredes = computed(() => {
    const verts = vertices.value;
    if (verts.length < 2) return [];

    const result = [];
    const count = isClosed.value ? verts.length : verts.length - 1;

    for (let i = 0; i < count; i++) {
        const a = verts[i];
        const b = verts[(i + 1) % verts.length];
        const longitud = Math.hypot((b.x - a.x) / gridStep, (b.y - a.y) / gridStep);
        const altura = Math.max(Number(camaraAltura.value) || 0.1, 0.1);

        result.push({
            index: i,
            isCierre: isClosed.value && i === verts.length - 1,
            longitud,
            superficie: longitud * altura,
            midX: (a.x + b.x) / 2,
            midY: (a.y + b.y) / 2,
        });
    }

    return result;
});

const perimetroTotal = computed(() => paredes.value.reduce((s, p) => s + p.longitud, 0));
const superficieTotal = computed(() => paredes.value.reduce((s, p) => s + p.superficie, 0));

const superficieCamara = computed(() => {
    const verts = vertices.value;
    if (!isClosed.value || verts.length < 3) return 0;

    let area = 0;
    for (let i = 0; i < verts.length; i++) {
        const a = verts[i];
        const b = verts[(i + 1) % verts.length];
        area += (a.x * b.y - b.x * a.y);
    }

    return Math.abs(area) / 2 / (gridStep * gridStep);
});

// ─── Paredes internas con medidas ───────────────────────────────────────────
const paredesInternasConMedidas = computed(() => {
    const altura = Math.max(Number(camaraAltura.value) || 0.1, 0.1);

    return paredesInternas.value.map((wall, idx) => {
        const longitud = wall.points.reduce((sum, p, i) => {
            if (i === 0) return sum;
            const prev = wall.points[i - 1];

            return sum + Math.hypot((p.x - prev.x) / gridStep, (p.y - prev.y) / gridStep);
        }, 0);

        return {
            id: wall.id,
            order: idx + 1,
            longitud,
            superficie: longitud * altura * 2,
        };
    });
});

// ─── Esquinas (convexas / cóncavas) ─────────────────────────────────────────
const esquinasInfo = computed(() => {
    const verts = vertices.value;
    if (!isClosed.value || verts.length < 3) {
        return { internas: 0, externas: 0, total: 0 };
    }

    let signedArea = 0;
    for (let i = 0; i < verts.length; i++) {
        const a = verts[i];
        const b = verts[(i + 1) % verts.length];
        signedArea += (a.x * b.y - b.x * a.y);
    }

    let internas = 0;
    let externas = 0;

    for (let i = 0; i < verts.length; i++) {
        const prev = verts[(i - 1 + verts.length) % verts.length];
        const curr = verts[i];
        const next = verts[(i + 1) % verts.length];

        const dx1 = curr.x - prev.x;
        const dy1 = curr.y - prev.y;
        const dx2 = next.x - curr.x;
        const dy2 = next.y - curr.y;

        const cross = dx1 * dy2 - dy1 * dx2;

        if (Math.abs(cross) < 0.001) continue;

        if (cross * signedArea > 0) {
            externas++;
        } else {
            internas++;
        }
    }

    return { internas, externas, total: internas + externas };
});

// ─── Emit design ─────────────────────────────────────────────────────────────
function panelesParaLongitud(longitud) {
    const ancho = Number(props.panelData?.ancho);
    if (!ancho || ancho <= 0) return null;

    return Math.ceil(longitud / ancho);
}

function sobrante(longitud) {
    const ancho = Number(props.panelData?.ancho);
    if (!ancho || ancho <= 0) return null;

    return Math.ceil(longitud / ancho) * ancho - longitud;
}

const totalPanelesCount = computed(() => {
    const ancho = Number(props.panelData?.ancho);
    if (!ancho || ancho <= 0) return null;

    const panelesPerimetro = paredes.value.reduce((sum, p) => sum + Math.ceil(p.longitud / ancho), 0);
    const panelesInternos = paredesInternasConMedidas.value.reduce((sum, pi) => sum + Math.ceil(pi.longitud / ancho), 0);

    return panelesPerimetro + panelesInternos;
});
const m2Muros = computed(() => {
    const ancho = Number(props.panelData?.ancho);
    if (!ancho || ancho <= 0) return null;

    return Math.ceil(perimetroTotal.value / ancho) * ancho * camaraAltura.value;
});
const designOutput = computed(() => ({
    paneles: [
        ...paredes.value.map(p => ({
            nombre: p.isCierre ? 'Cierre' : `Pared ${p.index + 1}`,
            ancho: p.longitud,
            alto: Math.max(Number(camaraAltura.value) || 0.1, 0.1),
            interna: false,
            panelCantidad: panelesParaLongitud(p.longitud),
            panelSobrante: sobrante(p.longitud),
        })),
        ...paredesInternasConMedidas.value.map(pi => ({
            nombre: `Pared int. ${pi.order}`,
            ancho: pi.longitud,
            alto: Math.max(Number(camaraAltura.value) || 0.1, 0.1),
            interna: true,
            panelCantidad: panelesParaLongitud(pi.longitud),
            panelSobrante: sobrante(pi.longitud),
        })),
    ],
    puertas: puertas.value.map(p => ({
        id: p.id,
        wallIndex: p.wallIndex,
        t: p.t,
        ancho: p.ancho,
        alto: p.alto,
        tipo: p.tipo,
        apertura: p.apertura,
        producto: p.producto ?? null,
    })),
    camara: {
        totalPaneles: totalPanelesCount.value,
        totalPanelesPerimetro: perimetroTotal.value / (Number(props.panelData?.ancho) || 1),
        perimetro: perimetroTotal.value,
        superficiePaneles: m2Muros.value + superficieCamara.value,
        superficieMuros: m2Muros.value,
        superficieCamara: superficieCamara.value,
        esquinasInternas: esquinasInfo.value.internas,
        esquinasExternas: esquinasInfo.value.externas,
        esquinasTotal: esquinasInfo.value.total,
        paredMasCorta: paredes.value.reduce((min, p) => p.longitud < min ? p.longitud : min, Infinity),
        design: {
            camaraAltura: camaraAltura.value,
            vertices: vertices.value.map(v => ({ x: v.x, y: v.y })),
            isClosed: isClosed.value,
            paredesInternas: paredesInternas.value.map(w => ({
                id: w.id,
                points: w.points.map(p => ({ x: p.x, y: p.y })),
            })),

        },
    },
}));

watch(designOutput, (val) => emit('update:design', val), { deep: true, immediate: true });

// ─── Restaurar diseño guardado ────────────────────────────────────────────────
watch(() => props.design, (val) => {
    if (!val) return;

    camaraAltura.value = val.camaraAltura ?? 3;
    vertices.value = val.vertices ? val.vertices.map(v => ({ x: v.x, y: v.y })) : [];
    isClosed.value = val.isClosed ?? false;
    paredesInternas.value = val.paredesInternas
        ? val.paredesInternas.map(w => ({ id: w.id, points: w.points.map(p => ({ x: p.x, y: p.y })) }))
        : [];

    if (paredesInternas.value.length > 0) {
        nextInternalId = Math.max(...paredesInternas.value.map(w => w.id)) + 1;
    }

    puertas.value = val.puertas
        ? val.puertas.map(p => ({ id: p.id, wallIndex: p.wallIndex, t: p.t, ancho: p.ancho, apertura: p.apertura, producto: p.producto ?? null }))
        : [];

    if (puertas.value.length > 0) {
        nextPuertaId = Math.max(...puertas.value.map(p => p.id)) + 1;
    }

    internalWallInProgress.value = [];
    mode.value = 'perimetro';
}, { immediate: true });

// ─── Interacción con el canvas ────────────────────────────────────────────────
function getCanvasCoords(event) {
    const canvas = canvas2d.value;
    const rect = canvas.getBoundingClientRect();
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const raw = {
        x: (event.clientX - rect.left) * (canvas.width / rect.width),
        y: (event.clientY - rect.top) * (canvas.height / rect.height),
    };
    // Invertir transformación de zoom
    const logical = {
        x: (raw.x - cx) / zoom.value + cx,
        y: (raw.y - cy) / zoom.value + cy,
    };

    return logical;
}

function isNearFirstVertex(pos) {
    if (vertices.value.length < 3) return false;
    const { x, y } = vertices.value[0];

    return Math.hypot(pos.x - x, pos.y - y) < 14;
}

function onCanvasMouseDown(event) {
    const pos = getCanvasCoords(event);
    const target = findAnyVertexNear(pos);

    if (target) {
        dragTarget.value = target;
        dragMoved.value = false;
    }
}

function onCanvasMouseUp() {
    dragTarget.value = null;
}

function onCanvasClick(event) {
    if (dragMoved.value) {
        dragMoved.value = false;
        return;
    }

    const pos = getCanvasCoords(event);
    const snapped = snapPos(pos);

    if (isClosed.value && mode.value === 'interna') {
        if (findAnyVertexNear(pos)) return;
        internalWallInProgress.value = [...internalWallInProgress.value, snapped];
        return;
    }

    if (isClosed.value && mode.value === 'puerta') {
        const snap = snapToPared(pos);
        if (snap) {
            puertas.value = [
                ...puertas.value,
                {
                    id: nextPuertaId++,
                    wallIndex: snap.wallIndex,
                    t: snap.t,
                    ancho: nuevaAnchoPuerta.value,
                    alto: null,
                    tipo: null,
                    apertura: aperturaPuerta.value,
                    producto: null,
                },
            ];
        }

        return;
    }

    if (isClosed.value) return;

    if (isNearFirstVertex(pos)) {
        closeFigure();
        return;
    }

    if (findVertexNear(pos) >= 0) return;

    vertices.value = [...vertices.value, snapped];
}

function onCanvasMouseMove(event) {
    const pos = getCanvasCoords(event);
    const snapped = snapPos(pos);
    mousePos.value = snapped;

    if (dragTarget.value) {
        dragMoved.value = true;
        const dt = dragTarget.value;

        if (dt.type === 'perimeter') {
            vertices.value = vertices.value.map((v, i) =>
                i === dt.idx ? { x: snapped.x, y: snapped.y } : v,
            );
        } else if (dt.type === 'internal') {
            paredesInternas.value = paredesInternas.value.map((wall, wi) =>
                wi !== dt.wallIdx ? wall : {
                    ...wall,
                    points: wall.points.map((p, pi) =>
                        pi === dt.ptIdx ? { x: snapped.x, y: snapped.y } : p,
                    ),
                },
            );
        } else if (dt.type === 'inprogress') {
            internalWallInProgress.value = internalWallInProgress.value.map((p, pi) =>
                pi === dt.ptIdx ? { x: snapped.x, y: snapped.y } : p,
            );
        } else if (dt.type === 'door') {
            const snap = snapToPared(pos, 60);
            if (snap) {
                puertas.value = puertas.value.map(p =>
                    p.id === dt.doorId ? { ...p, wallIndex: snap.wallIndex, t: snap.t } : p,
                );
            }
        }

        return;
    }

    hoveredTarget.value = findAnyVertexNear(pos);
}

function onCanvasMouseLeave() {
    mousePos.value = { x: -1, y: -1 };
    hoveredTarget.value = null;
    dragTarget.value = null;
    dragMoved.value = false;
}

function onCanvasDblClick() {
    if (isClosed.value && mode.value === 'interna') {
        internalWallInProgress.value = internalWallInProgress.value.slice(0, -1);
        finishInternalWall();
        return;
    }

    if (isClosed.value || vertices.value.length < 3) return;

    vertices.value = vertices.value.slice(0, -1);

    if (vertices.value.length >= 3) {
        closeFigure();
    }
}

function closeFigure() {
    if (vertices.value.length >= 3) {
        isClosed.value = true;
    }
}

function finishInternalWall() {
    if (internalWallInProgress.value.length < 2) return;

    paredesInternas.value = [
        ...paredesInternas.value,
        { id: nextInternalId++, points: [...internalWallInProgress.value] },
    ];
    internalWallInProgress.value = [];
}

function removeInternalWall(id) {
    paredesInternas.value = paredesInternas.value.filter(w => w.id !== id);
}

function removePuerta(id) {
    puertas.value = puertas.value.filter(p => p.id !== id);
}

function setPuertaApertura(id, apertura) {
    puertas.value = puertas.value.map(p => p.id === id ? { ...p, apertura } : p);
}

function setPuertaProducto(id, producto) {
    // BT220130PBPBU10
    // CR300240PBPBU06
    const codigo = producto.value.trim().toUpperCase();
    const tipo = codigo.substr(0, 2);
    if (tipo !== 'BT' && tipo !== 'CR') return
    const alto = parseInt(codigo.substr(2, 3)) / 100;
    const ancho = parseInt(codigo.substr(5, 3)) / 100;
    if (isNaN(alto) || isNaN(ancho)) return;
    puertas.value = puertas.value.map(p => p.id === id ? { ...p, producto, alto, ancho, tipo } : p);
}

const aperturaText = (value) => {
    switch (value) {
        case 'in-left': return 'Interior izquierda';
        case 'in-right': return 'Interior derecha';
        case 'out-left': return 'Exterior izquierda';
        case 'out-right': return 'Exterior derecha';
        default: return '';
    }
};

function setPuertaAncho(id, ancho) {
    if (ancho <= 0) return;
    puertas.value = puertas.value.map(p => p.id === id ? { ...p, ancho } : p);
}

function setWallLength(wallIndex, newLengthM) {
    if (newLengthM <= 0) return;

    const n = vertices.value.length;
    if (n < 2) return;

    const a = vertices.value[wallIndex];
    const b = vertices.value[(wallIndex + 1) % n];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const currentPx = Math.hypot(dx, dy);
    if (currentPx < 0.001) return;

    const scale = (newLengthM * gridStep) / currentPx;
    const endIdx = (wallIndex + 1) % n;

    vertices.value = vertices.value.map((v, i) =>
        i === endIdx ? { x: a.x + dx * scale, y: a.y + dy * scale } : v,
    );
}

function setInternalWallLength(wallId, newLengthM) {
    if (newLengthM <= 0) return;

    paredesInternas.value = paredesInternas.value.map((wall) => {
        if (wall.id !== wallId) return wall;

        const pts = wall.points;
        if (pts.length < 2) return wall;

        let totalPx = 0;
        for (let i = 1; i < pts.length; i++) {
            totalPx += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
        }
        if (totalPx < 0.001) return wall;

        const scale = (newLengthM * gridStep) / totalPx;
        const newPoints = [{ ...pts[0] }];

        for (let i = 1; i < pts.length; i++) {
            const prev = newPoints[i - 1];
            const origPrev = pts[i - 1];
            const dx = pts[i].x - origPrev.x;
            const dy = pts[i].y - origPrev.y;
            newPoints.push({ x: prev.x + dx * scale, y: prev.y + dy * scale });
        }

        return { ...wall, points: newPoints };
    });
}

function clearFigure() {
    vertices.value = [];
    isClosed.value = false;
    paredesInternas.value = [];
    internalWallInProgress.value = [];
    puertas.value = [];
    mode.value = 'perimetro';
}

function undoLast() {
    if (mode.value === 'puerta') {
        if (puertas.value.length > 0) {
            puertas.value = puertas.value.slice(0, -1);
        }

        return;
    }

    if (mode.value === 'interna') {
        if (internalWallInProgress.value.length > 0) {
            internalWallInProgress.value = internalWallInProgress.value.slice(0, -1);
            return;
        }

        if (paredesInternas.value.length > 0) {
            paredesInternas.value = paredesInternas.value.slice(0, -1);
            return;
        }

        return;
    }

    if (isClosed.value) {
        isClosed.value = false;
        internalWallInProgress.value = [];
        return;
    }

    if (vertices.value.length > 0) {
        vertices.value = vertices.value.slice(0, -1);
    }
}

// ─── Extrusión 3D ─────────────────────────────────────────────────────────────
const extrudedMesh = shallowRef(null);
const edgesMesh = shallowRef(null);
const gridHelper3d = new THREE.GridHelper(30, 30, 0x1a3060, 0x0d1530);

watchEffect((onCleanup) => {
    const verts = vertices.value;

    if (!isClosed.value || verts.length < 3) {
        extrudedMesh.value = null;
        edgesMesh.value = null;
        return;
    }

    const h = Math.max(Number(camaraAltura.value) || 0.1, 0.1);

    const mPts = verts.map(v => ({ x: v.x / gridStep, y: -v.y / gridStep }));
    const cx = mPts.reduce((s, p) => s + p.x, 0) / mPts.length;
    const cy = mPts.reduce((s, p) => s + p.y, 0) / mPts.length;
    const shapePts = mPts.map(p => new THREE.Vector2(p.x - cx, p.y - cy));

    const shape = new THREE.Shape(shapePts);
    const geo = new THREE.ExtrudeGeometry(shape, { depth: h, bevelEnabled: false });

    geo.rotateX(-Math.PI / 2);

    const mat = new THREE.MeshStandardMaterial({
        color: 0x90caf9,
        transparent: true,
        opacity: 0.65,
        side: THREE.DoubleSide,
    });
    extrudedMesh.value = new THREE.Mesh(geo, mat);

    const edgeGeo = new THREE.EdgesGeometry(geo);
    const edgeMat = new THREE.LineBasicMaterial({ color: 0x42a5f5 });
    edgesMesh.value = new THREE.LineSegments(edgeGeo, edgeMat);

    onCleanup(() => {
        geo.dispose();
        edgeGeo.dispose();
        mat.dispose();
        edgeMat.dispose();
    });
});

// ─── Paredes internas 3D ──────────────────────────────────────────────────────
const internalWallsGroup = shallowRef(null);

watchEffect((onCleanup) => {
    const allWalls = [
        ...paredesInternas.value,
        ...(internalWallInProgress.value.length >= 2
            ? [{ id: -1, points: internalWallInProgress.value }]
            : []),
    ];

    if (allWalls.length === 0) {
        internalWallsGroup.value = null;
        return;
    }

    const h = Math.max(Number(camaraAltura.value) || 0.1, 0.1);
    const mPts = vertices.value.map(v => ({ x: v.x / gridStep, y: -v.y / gridStep }));
    const cx = mPts.length > 0 ? mPts.reduce((s, p) => s + p.x, 0) / mPts.length : 0;
    const cy = mPts.length > 0 ? mPts.reduce((s, p) => s + p.y, 0) / mPts.length : 0;

    const toWorld = (px, py) => ({ x: px / gridStep - cx, z: -py / gridStep - cy });

    const group = new THREE.Group();
    const disposables = [];

    allWalls.forEach((wall) => {
        const pts = wall.points;
        if (pts.length < 2) return;

        const isInProgress = wall.id === -1;
        const color = isInProgress ? 0xffb74d : 0xffa726;

        for (let pi = 0; pi < pts.length - 1; pi++) {
            const a = toWorld(pts[pi].x, pts[pi].y);
            const b = toWorld(pts[pi + 1].x, pts[pi + 1].y);

            // Rectángulo vertical (4 aristas de la pared)
            const positions = new Float32Array([
                a.x, 0, a.z, b.x, 0, b.z,
                b.x, 0, b.z, b.x, h, b.z,
                b.x, h, b.z, a.x, h, a.z,
                a.x, h, a.z, a.x, 0, a.z,
            ]);

            const lineGeo = new THREE.BufferGeometry();
            lineGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
            const lineMat = new THREE.LineBasicMaterial({ color });
            group.add(new THREE.LineSegments(lineGeo, lineMat));
            disposables.push(lineGeo, lineMat);
        }
    });

    internalWallsGroup.value = group;

    onCleanup(() => disposables.forEach(d => d.dispose()));
});

// ─── Puertas 3D ───────────────────────────────────────────────────────────────
const doorsGroup3d = shallowRef(null);

watchEffect((onCleanup) => {
    if (!isClosed.value || puertas.value.length === 0 || vertices.value.length < 2) {
        doorsGroup3d.value = null;
        return;
    }

    const h = Math.max(Number(camaraAltura.value) || 0.1, 0.1);
    const verts = vertices.value;
    const mPts = verts.map(v => ({ x: v.x / gridStep, y: -v.y / gridStep }));
    const centX = mPts.reduce((s, p) => s + p.x, 0) / mPts.length;
    const centY = mPts.reduce((s, p) => s + p.y, 0) / mPts.length;

    const toWorld = (px, py) => ({ x: px / gridStep - centX, z: py / gridStep + centY });

    const group = new THREE.Group();
    const disposables = [];

    puertas.value.forEach((puerta) => {
        const { wallIndex, t, ancho } = puerta;
        const n = verts.length;
        if (wallIndex >= n) return;

        const a2d = verts[wallIndex];
        const b2d = verts[(wallIndex + 1) % n];
        const a = toWorld(a2d.x, a2d.y);
        const b = toWorld(b2d.x, b2d.y);

        const dx = b.x - a.x;
        const dz = b.z - a.z;
        const segLen = Math.hypot(dx, dz);
        if (segLen < 0.001) return;

        const ux = dx / segLen;
        const uz = dz / segLen;
        const halfAncho = ancho / 2;
        const tClamped = Math.max(halfAncho / segLen, Math.min(1 - halfAncho / segLen, t));

        const cx3 = a.x + tClamped * dx;
        const cz3 = a.z + tClamped * dz;
        const p1x = cx3 - ux * halfAncho;
        const p1z = cz3 - uz * halfAncho;
        const p2x = cx3 + ux * halfAncho;
        const p2z = cz3 + uz * halfAncho;
        const doorH = Math.min(h, 2.1);

        // Marco
        const outlinePositions = new Float32Array([
            p1x, 0,      p1z,     p2x, 0,      p2z,
            p2x, 0,      p2z,     p2x, doorH,  p2z,
            p2x, doorH,  p2z,     p1x, doorH,  p1z,
            p1x, doorH,  p1z,     p1x, 0,      p1z,
        ]);
        const lineGeo = new THREE.BufferGeometry();
        lineGeo.setAttribute('position', new THREE.BufferAttribute(outlinePositions, 3));
        const lineMat = new THREE.LineBasicMaterial({ color: 0x26c6da });
        group.add(new THREE.LineSegments(lineGeo, lineMat));
        disposables.push(lineGeo, lineMat);

        // Panel semitransparente
        const pGeo = new THREE.BufferGeometry();
        const pVerts = new Float32Array([
            p1x, 0,      p1z,
            p2x, 0,      p2z,
            p2x, doorH,  p2z,
            p1x, doorH,  p1z,
        ]);
        const pIdx = new Uint16Array([0, 1, 2, 0, 2, 3]);
        pGeo.setAttribute('position', new THREE.BufferAttribute(pVerts, 3));
        pGeo.setIndex(new THREE.BufferAttribute(pIdx, 1));
        pGeo.computeVertexNormals();
        const pMat = new THREE.MeshStandardMaterial({
            color: 0x26c6da,
            transparent: true,
            opacity: 0.35,
            side: THREE.DoubleSide,
        });
        group.add(new THREE.Mesh(pGeo, pMat));
        disposables.push(pGeo, pMat);
    });

    doorsGroup3d.value = group;
    onCleanup(() => disposables.forEach(d => d.dispose()));
});

const cameraPosition = computed(() => {
    if (!isClosed.value || vertices.value.length === 0) return [8, 6, 8];

    const maxR = Math.max(
        ...vertices.value.map(v => Math.hypot(v.x / gridStep, v.y / gridStep)),
        (Number(camaraAltura.value) || 3) / 2,
    );
    const d = maxR * 2.5 + 4;

    return [d, d * 0.75, d];
});

// ─── OrbitControls ────────────────────────────────────────────────────────────
const CamaraOrbitControls = defineComponent({
    name: 'CamaraOrbitControls',
    setup() {
        const { renderer, camera } = useTres();
        const { onBeforeRender } = useLoop();
        let controls = null;

        watch(
            camera,
            (cam) => {
                if (cam && !controls) {
                    controls = new OrbitControls(cam, renderer.domElement);
                    controls.enableDamping = true;
                    controls.dampingFactor = 0.06;
                    controls.minDistance = 0.5;
                    controls.maxDistance = 200;
                    controls.target.set(0, 0, 0);
                }
            },
            { immediate: true },
        );

        onBeforeRender(() => {
            controls?.update();
        });

        onUnmounted(() => {
            controls?.dispose();
        });

        return () => null;
    },
});
</script>

<style lang="scss" scoped>
.designer-section-label {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 2px;
    font-size: 11px;
    color: #546e7a;
    font-weight: 500;
    padding-left: 7px;
    border-left: 3px solid #1976d2;
    line-height: 1.5;
}

.designer-totals-row {
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
}

.designer-corners-row {
    background: linear-gradient(135deg, #37474f 0%, #263238 100%);
}

.designer-corners-label {
    color: rgba(255, 255, 255, 0.65);
}

.wall-len-input--dark {
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.25);
    color: #fff;

    &:focus {
        border-color: #26c6da;
        background: rgba(38, 198, 218, 0.15);
    }
}

// ─── Barra flotante de herramientas ─────────────────────────────────────────
.tools-floating-bar {
    position: absolute;
    bottom: 10px;
    left: 40%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 6px;
    background: rgba(18, 18, 42, 0.90);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(66, 165, 245, 0.25);
    border-radius: 10px;
    padding: 5px 10px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
    z-index: 10;
    white-space: nowrap;
}

.tools-group {
    display: flex;
    align-items: center;
    gap: 4px;
}

.tools-group-label {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.45);
    font-weight: 500;
    user-select: none;
}

.tools-separator {
    width: 1px;
    height: 20px;
    background: rgba(255, 255, 255, 0.15);
    margin: 0 4px;
}

// ─── Transición de la barra ──────────────────────────────────────────────────
.tool-bar-enter-active,
.tool-bar-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.tool-bar-enter-from,
.tool-bar-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(8px);
}

.wall-len-input {
    background: rgba(25, 118, 210, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.22);
    border-radius: 4px;
    color: #212121;
    padding: 1px 4px;
    font-size: 11px;
    outline: none;
    appearance: textfield;
    -moz-appearance: textfield;

    &:focus {
        border-color: #1976d2;
        background: rgba(25, 118, 210, 0.10);
    }

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
        opacity: 1;
        height: 14px;
    }

    &--amber {
        &:focus {
            border-color: #ffa726;
            background: rgba(255, 167, 38, 0.10);
        }
    }
}
</style>
